import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchAllData, scanAnswerSheet, gradeQuiz } from "./quiz3Utils";

const STEP_UPLOAD = "upload";
const STEP_SCANNING = "scanning";
const STEP_REVIEW = "review";
const STEP_SUBMITTING = "submitting";
const STEP_DONE = "done";

const SHEET_A4D = "a4d";
const SHEET_THPT3 = "thpt3";

// PQ-02 — nộp bài bằng ảnh chụp phiếu trả lời đã tô bút chì: chọn ảnh -> gọi
// API quét (OMRChecker) lấy đáp án đã nhận diện -> màn hình xác nhận/sửa lại
// (vì ảnh điện thoại chụp thực tế không đọc được 100% chính xác) -> nộp bài
// qua đúng /api/grade-quiz đã dùng cho luồng làm bài online (không tự chấm
// điểm/tự ghi lịch sử riêng ở đây).
// Loại phiếu "THPT 3 phần" (mẫu Bộ GD&ĐT): Phần I map vào câu 1..40 của đề
// như phiếu của app; SBD / Mã đề / Phần II / Phần III chỉ hiển thị để đối
// chiếu (đề hiện chỉ có câu nhiều lựa chọn nên chưa chấm được 2 phần này).
const Quiz3ScanSheet = () => {
  const { quizId } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [step, setStep] = useState(STEP_UPLOAD);
  const [sheetType, setSheetType] = useState(SHEET_A4D);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [scanError, setScanError] = useState("");
  const [reviewAnswers, setReviewAnswers] = useState({});
  const [sheetInfo, setSheetInfo] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const startedAtRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchAllData()
      .then((data) => {
        if (cancelled) return;
        const q = (data.quizzes || {})[quizId];
        if (!q) { setLoadError("Không tìm thấy đề thi này."); setLoading(false); return; }
        if (q.type !== "fixed") {
          setLoadError("Chỉ nộp bài bằng ảnh được với đề CỐ ĐỊNH (fixed).");
          setLoading(false);
          return;
        }
        const ids = q.questionIds || [];
        const qs = ids.map((id) => ((data.questions || {})[id] ? { id, ...data.questions[id] } : null)).filter(Boolean);
        setQuiz({ id: quizId, ...q });
        setQuestions(qs);
        startedAtRef.current = Date.now();
        setLoading(false);
      })
      .catch((err) => { if (!cancelled) { setLoadError(err.message || "Có lỗi khi tải đề thi."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [quizId]);

  const onPickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setFile(f);
    setScanError("");
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onScan = async () => {
    if (!file) return;
    setStep(STEP_SCANNING);
    setScanError("");
    try {
      const data = await scanAnswerSheet(quizId, file, sheetType);
      setReviewAnswers(data.detectedAnswers || {});
      setSheetInfo(data.sheet || null);
      setStep(STEP_REVIEW);
    } catch (err) {
      setScanError(err.message || "Không quét được ảnh này.");
      setStep(STEP_UPLOAD);
    }
  };

  const onSubmit = async () => {
    setStep(STEP_SUBMITTING);
    setSubmitError("");
    try {
      const data = await gradeQuiz({
        quizId,
        questionIds: quiz.questionIds,
        answers: reviewAnswers,
        startedAt: startedAtRef.current,
      });
      setResult(data);
      setStep(STEP_DONE);
    } catch (err) {
      setSubmitError(err.message || "Có lỗi khi nộp bài, vui lòng thử lại.");
      setStep(STEP_REVIEW);
    }
  };

  if (loading) return <div style={pageStyle}>Đang tải đề thi...</div>;
  if (loadError) {
    return (
      <div style={pageStyle}>
        <p style={{ color: "#dc2626" }}>{loadError}</p>
        <Link to="/quiz3/take">← Quay lại chọn đề</Link>
      </div>
    );
  }

  if (step === STEP_DONE && result) {
    return (
      <div style={pageStyle}>
        <h2>Kết quả</h2>
        <p style={{ fontSize: 20, fontWeight: 700 }}>
          {result.score}/{result.total} câu đúng ({Math.round((result.score / result.total) * 100)}%)
        </p>
        <p style={{ color: "#6b7280" }}>Bài nộp bằng ảnh chụp phiếu trả lời — đã lưu vào lịch sử làm bài.</p>
        <div style={{ marginTop: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Link to="/quiz3/take" style={secondaryBtnStyle}>Chọn đề khác</Link>
          <Link to="/quiz3/history" style={secondaryBtnStyle}>Xem lịch sử làm bài</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: "0 0 4px" }}>📷 Nộp bài bằng ảnh — {quiz.title}</h2>
      <p style={{ color: "#6b7280", marginTop: 0 }}>
        {sheetType === SHEET_THPT3 ? (
          <>Phiếu trả lời trắc nghiệm <b>THPT 3 phần</b> (mẫu Bộ GD&ĐT). Chụp ảnh hoặc chọn file PDF scan cả trang phiếu (thấy rõ các ô vuông đen). Phần I (câu 1–40) được điền vào đề này; SBD, Mã đề, Phần II, Phần III hiển thị để đối chiếu.</>
        ) : (
          <>Chỉ áp dụng cho phiếu đã in ở mục "🖨 In phiếu trả lời" của đúng đề này. Chụp ảnh <b>trang "Phiếu trả lời"</b> (thấy rõ đủ 4 dấu tròn ở góc), không cần chụp trang đề bài.</>
        )}
      </p>

      {(step === STEP_UPLOAD || step === STEP_SCANNING) && (
        <div style={cardStyle}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
            {[[SHEET_A4D, "Phiếu của app (A–D)"], [SHEET_THPT3, "Phiếu THPT 3 phần"]].map(([value, label]) => (
              <label key={value} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: sheetType === value ? 600 : 400 }}>
                <input
                  type="radio"
                  name="sheetType"
                  checked={sheetType === value}
                  onChange={() => { setSheetType(value); setScanError(""); setFile(null); setPreviewUrl(""); }}
                  disabled={step === STEP_SCANNING}
                />
                {label}
              </label>
            ))}
          </div>
          {/* Phiếu của app: mở thẳng camera như trước. Phiếu THPT: cho chọn cả
              file PDF scan (capture sẽ chặn chọn file nên bỏ). */}
          <input
            key={sheetType}
            type="file"
            accept={sheetType === SHEET_THPT3 ? "image/*,application/pdf" : "image/*"}
            capture={sheetType === SHEET_THPT3 ? undefined : "environment"}
            onChange={onPickFile}
            disabled={step === STEP_SCANNING}
          />
          {previewUrl && file && file.type === "application/pdf" && (
            <p style={{ marginTop: 12, color: "#374151" }}>📄 {file.name}</p>
          )}
          {previewUrl && file && file.type !== "application/pdf" && (
            <div style={{ marginTop: 12 }}>
              <img src={previewUrl} alt="Ảnh phiếu trả lời đã chọn" style={{ maxWidth: "100%", maxHeight: 400, borderRadius: 8, border: "1px solid #e5e7eb" }} />
            </div>
          )}
          {scanError && <p style={{ color: "#dc2626", marginTop: 10 }}>{scanError}</p>}
          <div style={{ marginTop: 14 }}>
            <button style={primaryBtnStyle} disabled={!file || step === STEP_SCANNING} onClick={onScan}>
              {step === STEP_SCANNING ? "Đang quét ảnh..." : "Quét bài"}
            </button>
          </div>
        </div>
      )}

      {(step === STEP_REVIEW || step === STEP_SUBMITTING) && (
        <div>
          <div style={{ margin: "0 0 14px", padding: "10px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, color: "#1e40af", fontSize: 13 }}>
            Đã quét xong — kiểm tra lại đáp án bên dưới trước khi nộp. Câu tô vàng là câu máy KHÔNG đọc được rõ, hãy tự chọn tay.
          </div>
          {sheetInfo && <ThptSheetSummary sheet={sheetInfo} />}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {questions.map((q, idx) => {
              const unclear = reviewAnswers[q.id] === undefined || reviewAnswers[q.id] === null;
              return (
                <div key={q.id} style={{ ...reviewCardStyle, borderColor: unclear ? "#fde68a" : "#e5e7eb", background: unclear ? "#fffbeb" : "#fff" }}>
                  <p style={{ margin: "0 0 8px", fontWeight: 600 }}>Câu {idx + 1}: {q.content}</p>
                  {(q.options || []).map((opt) => (
                    <label key={opt.id} style={optionRowStyle(reviewAnswers[q.id] === opt.id)}>
                      <input
                        type="radio"
                        name={`q-${q.id}`}
                        checked={reviewAnswers[q.id] === opt.id}
                        onChange={() => setReviewAnswers((a) => ({ ...a, [q.id]: opt.id }))}
                        disabled={step === STEP_SUBMITTING}
                      />
                      {opt.text}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
          {submitError && <p style={{ color: "#dc2626", marginTop: 10 }}>{submitError}</p>}
          <div style={{ marginTop: 16 }}>
            <button style={primaryBtnStyle} disabled={step === STEP_SUBMITTING} onClick={onSubmit}>
              {step === STEP_SUBMITTING ? "Đang nộp bài..." : "Xác nhận nộp bài"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Kết quả đọc phiếu THPT 3 phần: SBD, Mã đề, Phần II (Đúng/Sai), Phần III
// (điền số) + cảnh báo — chỉ để xem/đối chiếu, không gửi lên khi nộp bài.
const ThptSheetSummary = ({ sheet }) => {
  const tf = (v) => (v === true ? "Đ" : v === false ? "S" : "–");
  const shown = (s) => (s ? s.replace(/_/g, "·") : "(trống)");
  return (
    <div style={{ ...cardStyle, marginBottom: 14, fontSize: 14 }}>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 10 }}>
        <div>Số báo danh: <b style={{ fontFamily: "monospace", fontSize: 16 }}>{shown(sheet.sbd)}</b></div>
        <div>Mã đề: <b style={{ fontFamily: "monospace", fontSize: 16 }}>{shown(sheet.maDe)}</b></div>
      </div>
      <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Phần II — Đúng/Sai</p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", marginBottom: 10 }}>
          <thead>
            <tr>
              <th style={thtdStyle}></th>
              {(sheet.part2 || []).map((_, i) => <th key={i} style={thtdStyle}>Câu {i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {["a", "b", "c", "d"].map((k) => (
              <tr key={k}>
                <td style={thtdStyle}>{k})</td>
                {(sheet.part2 || []).map((ans, i) => <td key={i} style={thtdStyle}>{tf(ans[k])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Phần III — Trả lời ngắn</p>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {(sheet.part3 || []).map((v, i) => (
          <div key={i}>Câu {i + 1}: <b style={{ fontFamily: "monospace" }}>{v || "–"}</b></div>
        ))}
      </div>
      {(sheet.warnings || []).length > 0 && (
        <ul style={{ margin: "10px 0 0", paddingLeft: 18, color: "#b45309" }}>
          {sheet.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
    </div>
  );
};

const thtdStyle = { border: "1px solid #e5e7eb", padding: "4px 8px", textAlign: "center" };

const pageStyle = { padding: 24, maxWidth: 800, margin: "0 auto" };

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
  background: "#fff",
};

const reviewCardStyle = {
  border: "1px solid",
  borderRadius: 10,
  padding: 14,
  background: "#fff",
};

const optionRowStyle = (checked) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  border: "1px solid " + (checked ? "#93c5fd" : "#e5e7eb"),
  background: checked ? "#eff6ff" : "#fff",
  borderRadius: 8,
  marginBottom: 8,
  cursor: "pointer",
});

const primaryBtnStyle = {
  padding: "8px 18px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
};

const secondaryBtnStyle = {
  padding: "8px 18px",
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 600,
  display: "inline-block",
};

export default Quiz3ScanSheet;
